import { Box, Container, Stack, Typography } from '@mui/material';
import styles from './PulseSection.module.scss'
import zIndex from '@mui/material/styles/zIndex';
import IconCard from '../IconCard/IconCard';
import searchBlue from '../../../assets/img/webp/search.webp';
import trustBlue from '../../../assets/img/webp/handshake.webp';
import walletBlue from '../../../assets/img/webp/wallet.webp';
import searchWhite from '../../../assets/img/pulse/search-white.svg';
import trustWhite from '../../../assets/img/pulse/handshake-white.svg';
import walletWhite from '../../../assets/img/pulse/wallet-white.svg';

interface PulseCard {
    heading: string;
    body: string;
    desktopImg: string;
    mobileImg: string;
    alt: string;
    imgWidth?: string;
    imgHeight?: string;
}

const pulseBorderRadius = {
    xs: '3rem',
    md: '8rem'
};

const sectionContainerSx = {
    marginTop: '0rem',
    marginBottom: '2.5rem',
};

const pulseSectionSx = {
    width: '100%',
    position: 'relative',
    backgroundColor: 'primary.main',
    paddingX: { xs: '1.6rem', md: '6rem' },
    paddingY: { xs: '6.1rem', md: '9rem' },
};

const pulseWaveTop1Sx = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: pulseBorderRadius,
    backgroundColor: 'common.white',
    borderBottomRightRadius: pulseBorderRadius,
    zIndex: 1,
};

const pulseWaveTop2Sx = {
    position: 'absolute',
    top: pulseBorderRadius,
    left: 0,
    width: '10%',
    height: pulseBorderRadius,
    backgroundColor: 'common.white',
    zIndex: 2,
};

const pulseWaveTop3Sx = {
    position: 'absolute',
    top: pulseBorderRadius,
    left: 0,
    width: '10%',
    height: pulseBorderRadius,
    backgroundColor: 'primary.main',
    borderTopLeftRadius: pulseBorderRadius,
    zIndex: 3,
};

const pulseWaveBottom1Sx = {
    position: 'absolute',
    bottom: 0,
    right: 0,
    width: '100%',
    height: pulseBorderRadius,
    backgroundColor: 'common.white',
    borderTopLeftRadius: pulseBorderRadius,
    zIndex: 1,
};

const pulseWaveBottom2Sx = {
    position: 'absolute',
    bottom: pulseBorderRadius,
    right: 0,
    width: '10%',
    height: pulseBorderRadius,
    backgroundColor: 'common.white',
    zIndex: 2,
};

const pulseWaveBottom3Sx = {
    position: 'absolute',
    bottom: pulseBorderRadius,
    right: 0,
    width: '10%',
    height: pulseBorderRadius,
    backgroundColor: 'primary.main',
    borderBottomRightRadius: pulseBorderRadius,
    zIndex: 3,
};

const pulseContentSx = {
    position: 'relative',
    marginTop: { xs: '0.5rem', md: '2rem' },
};

const pulseTextSx = {
    textAlign: 'center',
    marginTop: { md: '8rem' },
    gap: { xs: '3rem', md: '5rem' },
    color: 'common.white',
    maxWidth: { xs: '100%', md: '120rem' },
    marginX: 'auto',
};

const desktopCardContainerSx = {
    display: { xs: 'none', md: 'flex' },
    justifyContent: 'center',
    marginTop: { md: '10rem' },
    marginBottom: '5rem',
};

const mobileCardContainerSx = {
    display: { xs: 'flex', md: 'none' },
    justifyContent: 'center',
    marginTop: 5,
};

const desktopCardStackSx = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    width: '1100px',
    justifyContent: 'space-between',
    color: 'common.white',
};

const mobileCardStackSx = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
};

const pulseCards: PulseCard[] = [
    {
        heading: "We're flexible",
        body: "You can save interest by repaying your short term loan early. If things go wrong, we're here to help you find a solution.",
        desktopImg: walletWhite,
        mobileImg: walletBlue,
        alt: 'Wallet icon',
    },
    {
        heading: "We're committed to you",
        body: "We lend responsibly, treat our customers fairly, and help people make sense of their money.",
        desktopImg: trustWhite,
        mobileImg: trustBlue,
        alt: 'Handshake icon',
    },
    {
        heading: "We're transparent",
        body: 'There are no hidden charges. We make all our fees completely clear upfront before you apply.',
        desktopImg: searchWhite,
        mobileImg: searchBlue,
        alt: 'Search icon',
        imgWidth: '60px',
        imgHeight: '91px',
    },
];

function PulseSection() {
    const renderCards = (imgKey: 'desktopImg' | 'mobileImg') =>
        pulseCards.map(({ heading, body, desktopImg, mobileImg, alt, imgWidth, imgHeight }) => (
            <IconCard
                key={heading}
                heading={heading}
                body={body}
                img={imgKey === 'desktopImg' ? desktopImg : mobileImg}
                alt={alt}
                imgWidth={imgWidth}
                imgHeight={imgHeight}
            />
        ));

    return (
        <Container
            sx={sectionContainerSx}
            disableGutters
            maxWidth={false}
        >
            <Box sx={pulseSectionSx}>
                <Box aria-hidden sx={pulseWaveTop1Sx} />
                <Box aria-hidden sx={pulseWaveTop2Sx} />
                <Box aria-hidden sx={pulseWaveTop3Sx} />
                <Box aria-hidden sx={pulseWaveBottom1Sx} />
                <Box aria-hidden sx={pulseWaveBottom2Sx} />
                <Box aria-hidden sx={pulseWaveBottom3Sx} />

                <Box sx={pulseContentSx}>
                    <Stack sx={pulseTextSx}>
                        <Typography
                            variant="h1"
                            fontWeight={400}
                            fontSize={{ xs: '2.4rem', md: '4rem' }}
                        >
                            Flexible, short term loans that give you back control.
                        </Typography>
                        <Typography
                            variant="h3"
                            fontSize={{ xs: '20px' }}
                            fontWeight={{ xs: 200, md: 400 }}
                        >
                            Our{' '}
                            <a className={styles['home-link']} href="/short-term-loan">
                                short term loans
                            </a>{' '}
                            help people manage their cash flow. If you need a quick loan to tide you over for a short
                            while, we&apos;re here for you. Our{' '}
                            <a className={styles['home-link']} href="/personal-loans">
                                personal loan
                            </a>{' '}
                            process is simple and easy to understand. To find out more,
                            visit{' '}
                            <a className={styles['home-link']} href="/how-it-works">
                                how it works.
                            </a>
                        </Typography>
                    </Stack>

                    <Box sx={desktopCardContainerSx}>
                        <Stack sx={desktopCardStackSx}>
                            {renderCards('desktopImg')}
                        </Stack>
                    </Box>
                </Box>
            </Box>

            <Box sx={mobileCardContainerSx}>
                <Stack sx={mobileCardStackSx}>
                    {renderCards('mobileImg')}
                </Stack>
            </Box>
        </Container>
    )
};

export default PulseSection
